import React, { useState, useEffect } from 'react';
import { dealerActivityLogApi, dealerSalesRepsApi } from '@/services/api';
import { ActivityLogEntry, DealerSalesRep } from '@/types';

const actionColors: Record<string, string> = {
  create: 'text-green-400',
  update: 'text-blue-400',
  delete: 'text-red-400',
  login: 'text-purple-400',
  password_reset: 'text-yellow-400',
  mark_paid: 'text-green-500',
  default: 'text-gray-400',
};

export function ActivityLogPage() {
  const [activities, setActivities] = useState<ActivityLogEntry[]>([]);
  const [salesReps, setSalesReps] = useState<DealerSalesRep[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [userFilter, setUserFilter] = useState<string>('all');
  const [actionFilter, setActionFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalActivities, setTotalActivities] = useState(0);
  const [pageSize] = useState(50);

  useEffect(() => {
    fetchSalesReps();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [userFilter, actionFilter, dateFrom, dateTo]);

  useEffect(() => {
    fetchActivities();
  }, [userFilter, actionFilter, dateFrom, dateTo, page]);

  const fetchSalesReps = async () => {
    try {
      const response = await dealerSalesRepsApi.list({ status: 'active', limit: 100 });
      setSalesReps(response?.salesReps || []);
    } catch (err: any) {
      console.error('Failed to load sales reps:', err);
    }
  };

  const fetchActivities = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {
        page,
        limit: pageSize,
      };

      if (userFilter !== 'all') {
        params.userId = userFilter;
      }

      if (actionFilter !== 'all') {
        params.action = actionFilter;
      }

      if (dateFrom) {
        params.startDate = dateFrom;
      }

      if (dateTo) {
        params.endDate = dateTo;
      }

      const response = await dealerActivityLogApi.list(params);
      setActivities(response?.activities || []);
      setTotalPages(response?.pagination?.pages || 1);
      setTotalActivities(response?.pagination?.total || 0);
    } catch (err: any) {
      setError(err.message || 'Failed to load activity log');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setUserFilter('all');
    setActionFilter('all');
    setDateFrom('');
    setDateTo('');
  };

  const hasActiveFilters = userFilter !== 'all' || actionFilter !== 'all' || dateFrom || dateTo;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActionColor = (action: string): string => {
    const lowerAction = action.toLowerCase();
    if (lowerAction.includes('create')) return actionColors.create;
    if (lowerAction.includes('update') || lowerAction.includes('edit')) return actionColors.update;
    if (lowerAction.includes('delete') || lowerAction.includes('deactivate')) return actionColors.delete;
    if (lowerAction.includes('login')) return actionColors.login;
    if (lowerAction.includes('password')) return actionColors.password_reset;
    if (lowerAction.includes('paid')) return actionColors.mark_paid;
    return actionColors.default;
  };

  const getActionIcon = (action: string) => {
    const lowerAction = action.toLowerCase();

    if (lowerAction.includes('create')) {
      return (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
        </svg>
      );
    }

    if (lowerAction.includes('update') || lowerAction.includes('edit')) {
      return (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      );
    }

    if (lowerAction.includes('delete') || lowerAction.includes('deactivate')) {
      return (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      );
    }

    if (lowerAction.includes('login')) {
      return (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
        </svg>
      );
    }

    // Default icon
    return (
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Activity Log</h1>
        <p className="text-gray-400 mt-1">Track all administrative actions and changes</p>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap items-center gap-4">
          {/* User Filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-400">User:</label>
            <select
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              className="select-field py-2 px-3 text-sm"
            >
              <option value="all">All Users</option>
              {salesReps.map((rep) => (
                <option key={rep.id} value={rep.id}>
                  {rep.firstName} {rep.lastName}
                </option>
              ))}
            </select>
          </div>

          {/* Action Filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-400">Action:</label>
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="select-field py-2 px-3 text-sm"
            >
              <option value="all">All Actions</option>
              <option value="create">Create</option>
              <option value="update">Update</option>
              <option value="delete">Delete</option>
              <option value="login">Login</option>
              <option value="password_reset">Password Reset</option>
              <option value="mark_paid">Mark as Paid</option>
            </select>
          </div>

          {/* Date Range */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-400">From:</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="input-field py-2 px-3 text-sm"
            />
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-400">To:</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="input-field py-2 px-3 text-sm"
            />
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-sm text-keder-yellow hover:text-yellow-400 font-medium flex items-center gap-1"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="card bg-red-900/20 border-red-800">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Results Count */}
      {!loading && (
        <div className="text-sm text-gray-400">
          Showing {(activities || []).length} of {totalActivities} activities
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="card">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-keder-gray border-t-keder-yellow"></div>
          </div>
        </div>
      ) : (activities || []).length === 0 ? (
        /* Empty State */
        <div className="card text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-white">No activities found</h3>
          <p className="mt-1 text-sm text-gray-400">
            {hasActiveFilters ? 'Try adjusting your filters.' : 'Activity log will appear here as actions are performed.'}
          </p>
        </div>
      ) : (
        /* Activity Timeline */
        <div className="card">
          <div className="space-y-4">
            {(activities || []).map((activity, index) => (
              <div
                key={activity.id}
                className={`flex items-start gap-4 pb-4 ${index !== (activities || []).length - 1 ? 'border-b border-keder-gray/50' : ''}`}
              >
                {/* Icon */}
                <div className={`flex-shrink-0 p-2 rounded-lg ${getActionColor(activity.action)}`}>
                  {getActionIcon(activity.action)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      {/* Action Description */}
                      <p className="text-white font-medium">
                        {activity.description || activity.action}
                      </p>

                      {/* User & Timestamp */}
                      <div className="flex items-center gap-2 mt-1 text-sm text-gray-400">
                        <span>{activity.userName || `${activity.userFirstName} ${activity.userLastName}`}</span>
                        <span>•</span>
                        <span>{formatDate(activity.createdAt)}</span>
                        {activity.entityType && (
                          <>
                            <span>•</span>
                            <span className="capitalize">{activity.entityType.replace('_', ' ')}</span>
                          </>
                        )}
                      </div>

                      {/* Details */}
                      {activity.details && Object.keys(activity.details).length > 0 && (
                        <div className="mt-2 p-3 bg-keder-gray/30 rounded-lg text-xs">
                          <pre className="text-gray-300 overflow-x-auto">
                            {JSON.stringify(activity.details, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>

                    {/* User Type Badge */}
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      activity.userType === 'dealer_contact'
                        ? 'bg-purple-900/30 text-purple-300'
                        : 'bg-blue-900/30 text-blue-300'
                    }`}>
                      {activity.userType === 'dealer_contact' ? 'Admin' : 'Sales Rep'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="card">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-400">
              Page {page} of {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-4 py-2 bg-keder-gray text-white rounded-lg hover:bg-keder-gray/80 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 bg-keder-gray text-white rounded-lg hover:bg-keder-gray/80 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
